// src/prototype/exports/exportArtifactPdf.ts
// ============================================================
// Approval-artifact PDF export — WYSIWYG capture of the live UI.
// Uses html-to-image + jsPDF page packing (section-aware) so the
// PDF matches ArtifactPreviewModal and avoids html2pdf slice bugs.
// ============================================================

import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import type { DvaArtifact } from '../artifacts/types/artifactTypes';
import { ARTIFACT_DISPLAY_LABELS } from '../artifacts/types/artifactTypes';
import { buildFilename } from './downloadHelper';

export interface ExportArtifactPdfOptions {
  /** Live preview root element id */
  elementId: string;
  artifact: DvaArtifact;
  clientIdentifier?: string;
}

const EXPORT_WIDTH_PX = 672; // matches modal max-w-2xl
const PDF_MARGIN_PT = 28;
const PAGE_FORMAT = 'letter' as const;
const BACKGROUND = '#F5F7FA';

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function measureChildren(root: HTMLElement): Array<{ top: number; height: number }> {
  const rootRect = root.getBoundingClientRect();
  const nodes = root.querySelectorAll<HTMLElement>('[data-export-block]');
  const targets = nodes.length > 0
    ? Array.from(nodes)
    : Array.from(root.children).filter((node): node is HTMLElement => node instanceof HTMLElement);

  return targets.map((el) => {
    const rect = el.getBoundingClientRect();
    return {
      top: rect.top - rootRect.top,
      height: Math.max(1, rect.height),
    };
  });
}

/** Pack UI blocks onto pages without splitting when they fit. */
function buildPageCrops(
  blocks: Array<{ top: number; height: number }>,
  totalHeight: number,
  pageContentHeightCss: number,
): Array<{ top: number; height: number }> {
  if (blocks.length === 0) {
    return [{ top: 0, height: Math.max(1, totalHeight) }];
  }

  const pages: Array<{ top: number; height: number }> = [];
  let pageTop = 0;
  let pageBottom = 0;

  const flush = () => {
    const height = Math.max(1, pageBottom - pageTop);
    pages.push({ top: pageTop, height });
  };

  for (const block of blocks) {
    const blockBottom = block.top + block.height;
    const wouldExceed = blockBottom - pageTop > pageContentHeightCss + 1;

    if (wouldExceed && pageBottom > pageTop) {
      flush();
      pageTop = block.top;
      pageBottom = blockBottom;

      // Block taller than a page — hard-slice it
      while (pageBottom - pageTop > pageContentHeightCss + 1) {
        pages.push({ top: pageTop, height: pageContentHeightCss });
        pageTop += pageContentHeightCss;
      }
    } else {
      pageBottom = Math.max(pageBottom, blockBottom);
    }
  }

  if (pageBottom > pageTop) flush();

  // Make crops contiguous so Tailwind gaps (space-y-*) are not dropped between pages
  const contiguous: Array<{ top: number; height: number }> = [];
  for (let i = 0; i < pages.length; i++) {
    const top = i === 0 ? 0 : pages[i].top;
    const nextTop = i + 1 < pages.length ? pages[i + 1].top : totalHeight;
    contiguous.push({ top, height: Math.max(1, nextTop - top) });
  }
  return contiguous.length ? contiguous : [{ top: 0, height: Math.max(1, totalHeight) }];
}

function cropCanvas(
  source: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
): HTMLCanvasElement {
  const cropped = document.createElement('canvas');
  cropped.width = Math.max(1, Math.floor(sw));
  cropped.height = Math.max(1, Math.floor(sh));
  const ctx = cropped.getContext('2d');
  if (!ctx) throw new Error('Could not crop the captured artifact image.');
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, cropped.width, cropped.height);
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, cropped.width, cropped.height);
  return cropped;
}

/**
 * Capture the live artifact preview DOM and download a letter PDF that
 * matches the UI layout (colors, cards, tables, spacing).
 */
export async function exportArtifactPdf(options: ExportArtifactPdfOptions): Promise<string> {
  const { artifact, elementId } = options;
  const target = document.getElementById(elementId);
  if (!target) {
    throw new Error('Artifact preview is not ready to export.');
  }

  const typeLabel = ARTIFACT_DISPLAY_LABELS[artifact.artifactType] || 'Artifact';
  const clientPart =
    sanitizeFilenamePart(options.clientIdentifier || artifact.clientId || 'Client') || 'Client';
  const typePart = sanitizeFilenamePart(typeLabel) || 'Artifact';
  const initiativePart = artifact.subtitle || artifact.title
    ? `_${sanitizeFilenamePart(artifact.subtitle || artifact.title)}`
    : '';
  const filename = buildFilename(clientPart, `${typePart}${initiativePart}`, 'pdf');

  // Expand scroll container so the full preview is painted for capture
  const scrollParent = target.closest('.overflow-y-auto') as HTMLElement | null;
  const prevParentMaxH = scrollParent?.style.maxHeight ?? '';
  const prevParentOverflow = scrollParent?.style.overflow ?? '';
  if (scrollParent) {
    scrollParent.style.maxHeight = 'none';
    scrollParent.style.overflow = 'visible';
  }

  // Offscreen fixed-width clone for stable layout (same width as modal content)
  const host = document.createElement('div');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${EXPORT_WIDTH_PX}px`,
    'padding:0',
    `background:${BACKGROUND}`,
    'z-index:-1',
    'pointer-events:none',
  ].join(';');

  const clone = target.cloneNode(true) as HTMLElement;
  clone.id = `${elementId}-print-clone`;
  clone.style.cssText = [
    `width:${EXPORT_WIDTH_PX}px`,
    'max-width:none',
    'margin:0',
    'padding:0',
    `background:${BACKGROUND}`,
    'box-sizing:border-box',
  ].join(';');
  host.appendChild(clone);
  document.body.appendChild(host);

  let canvas: HTMLCanvasElement;
  try {
    await waitForPaint();

    const cssHeight = Math.max(clone.scrollHeight, clone.offsetHeight, 1);
    const pixelRatio = Math.min(2, Math.max(1, 16384 / Math.max(EXPORT_WIDTH_PX, cssHeight)));

    canvas = await toCanvas(clone, {
      width: EXPORT_WIDTH_PX,
      height: cssHeight,
      pixelRatio,
      backgroundColor: BACKGROUND,
      cacheBust: true,
      skipFonts: false,
    });

    const ratio = canvas.width / EXPORT_WIDTH_PX;
    const totalHeightCss = canvas.height / ratio;
    const blocks = measureChildren(clone);

    // Letter content area in CSS px at EXPORT_WIDTH_PX → page width mapping
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: PAGE_FORMAT });
    const pageWidthPt = pdf.internal.pageSize.getWidth();
    const pageHeightPt = pdf.internal.pageSize.getHeight();
    const contentWidthPt = pageWidthPt - PDF_MARGIN_PT * 2;
    const contentHeightPt = pageHeightPt - PDF_MARGIN_PT * 2;
    const pageContentHeightCss = (contentHeightPt / contentWidthPt) * EXPORT_WIDTH_PX;

    const pages = buildPageCrops(blocks, totalHeightCss, pageContentHeightCss);

    pages.forEach((page, index) => {
      if (index > 0) pdf.addPage();

      const sx = 0;
      const sy = Math.floor(page.top * ratio);
      const sw = canvas.width;
      const sh = Math.min(canvas.height - sy, Math.ceil(page.height * ratio));
      const slice = cropCanvas(canvas, sx, sy, sw, sh);

      const imgWidthPt = contentWidthPt;
      const imgHeightPt = (slice.height / slice.width) * imgWidthPt;
      const imgData = slice.toDataURL('image/jpeg', 0.95);

      pdf.addImage(
        imgData,
        'JPEG',
        PDF_MARGIN_PT,
        PDF_MARGIN_PT,
        imgWidthPt,
        Math.min(imgHeightPt, contentHeightPt),
        undefined,
        'FAST',
      );
    });

    pdf.save(filename);
    return filename;
  } finally {
    host.remove();
    if (scrollParent) {
      scrollParent.style.maxHeight = prevParentMaxH;
      scrollParent.style.overflow = prevParentOverflow;
    }
  }
}
